import React from "react";
import { Helmet } from "react-helmet-async";
import {
  SITE_ORIGIN,
  DEFAULT_OG_IMAGE,
  brandMarkLogo,
  robotsContent,
} from "../SEO/siteOrigin";
import { faqPageJsonLd } from "../SEO/faqPageJsonLd";

const SupportSEO = ({
  title,
  description,
  keywords = [],
  canonicalUrl,
  article = false,
  publishedTime,
  modifiedTime,
  author,
  image,
  type = "website",
  faq = [],
}) => {
  const defaultTitle = "Bowling Tournament Support - Help Center & FAQs";
  const defaultDescription =
    "Get help with bowling tournament management. Find tutorials, FAQs, and support for creating and managing bowling tournaments.";
  const defaultKeywords = [
    "bowling tournament support",
    "bowling tournament help",
    "tournament management",
    "bowling tournament FAQ",
    "tournament creation guide",
    "bowling tournament tutorials",
  ];

  const seoTitle = title || defaultTitle;
  const seoDescription = description || defaultDescription;
  const seoKeywords = [...defaultKeywords, ...keywords].join(", ");
  const seoPath = canonicalUrl
    ? canonicalUrl.startsWith("http")
      ? new URL(canonicalUrl).pathname
      : canonicalUrl
    : typeof window !== "undefined"
      ? window.location.pathname
      : "/";
  const seoUrl = `${SITE_ORIGIN}${seoPath.startsWith("/") ? seoPath : `/${seoPath}`}`;
  const seoImage = image
    ? image.startsWith("http")
      ? image
      : `${SITE_ORIGIN}${image}`
    : `${SITE_ORIGIN}${DEFAULT_OG_IMAGE}`;
  const faqStructuredData = faqPageJsonLd(faq);

  const getStructuredData = () => {
    if (article) {
      return {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: seoTitle,
        description: seoDescription,
        image: seoImage,
        author: {
          "@type": "Person",
          name: author || "Bowling Tournament App",
        },
        publisher: {
          "@type": "Organization",
          name: "Bowling Tournament App",
          logo: brandMarkLogo(),
        },
        datePublished: publishedTime || new Date().toISOString(),
        dateModified: modifiedTime || new Date().toISOString(),
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": seoUrl,
        },
      };
    }

    return {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: seoTitle,
      description: seoDescription,
      url: seoUrl,
    };
  };

  return (
    <Helmet prioritizeSeoTags>
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <link rel="canonical" href={seoUrl} />

      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:image:alt" content="Bowling Tournament App" />
      <meta property="og:site_name" content="Bowling Tournament App" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />

      <meta name="robots" content={robotsContent()} />
      <meta name="googlebot" content={robotsContent()} />
      <meta name="author" content={author || "Bowling Tournament App"} />
      {publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}

      <script type="application/ld+json">
        {JSON.stringify(getStructuredData())}
      </script>

      {faqStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      )}

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: SITE_ORIGIN,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Support",
              item: `${SITE_ORIGIN}/support`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: title || "Help Center",
              item: seoUrl,
            },
          ],
        })}
      </script>
    </Helmet>
  );
};

export default SupportSEO;
